import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { gql } from "@apollo/client";
import secureLocalStorage from "react-secure-storage";
import { DeviceType, VideoStateType } from "../../types/video";
import { ApolloClientType } from "../../types/apollo";
import type { RootState } from "../../types/store";
import { logOut } from "./user";

const API_BASE_URL = "https://api.videosdk.live";

const initialState: VideoStateType = {
  token: null,
  customRoomId: null,
  loading: false,
  roomId: null,
  isMeetingStarted: false,
  isMicOn: false,
  isWebcamOn: false,
  currentMic: { id: null, label: null },
  currentWebcam: { id: null, label: null },
  currentSpeaker: { id: null, label: null },
  cameraPermissionAllowed: false,
  microphonePermissionAllowed: false,
};

export const GET_JWT = gql`
  mutation getJwt($id: Int!) {
    GenerateVideoSDKJWT(appointment_id: $id) {
      jwt
      room_id
    }
  }
`;

export const fetchToken = createAsyncThunk(
  "video/fetch",
  async (appointmentId: number, { extra, rejectWithValue }) => {
    const { apolloClient } = extra as { apolloClient: ApolloClientType };

    const { data, errors } = await apolloClient.mutate({
      mutation: GET_JWT,
      variables: {
        id: appointmentId
      },
      fetchPolicy: "network-only"
    });

    if (errors) {
      return rejectWithValue(errors[0].message);
    }

    return { ...data.GenerateVideoSDKJWT };
  },
);

export const createRoom = createAsyncThunk(
  "video/create",
  async (_, { getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const { token, customRoomId } = state.video;

    if (!token) {
      return rejectWithValue("No authorization token provided");
    }

    const response = await fetch(
      `${API_BASE_URL}/v2/rooms`,
      {
        method: "POST",
        headers: {
          Authorization: token,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          "customRoomId": customRoomId,
        }),
      }
    );

    if (response.status === 400) {
      const data = await response.text();
      return rejectWithValue(data);
    }

    const data = await response.json();

    if (data.roomId) {
      return { meetingId: data.roomId };
    }

    return rejectWithValue(data.error);
  },
);

export const videoSlice = createSlice({
  name: "video",
  initialState,
  reducers: {
    endMeeting() {
      secureLocalStorage.removeItem("appointmentId");
      return initialState;
    },
    startMeeting(state) {
      state.isMeetingStarted = true;
    },
    setIsMicOn: (state, action: PayloadAction<boolean>) => {
      state.isMicOn = action.payload;
    },
    setIsWebcamOn: (state, action: PayloadAction<boolean>) => {
      state.isWebcamOn = action.payload;
    },
    setCurrentMicrophone: (state, action: PayloadAction<DeviceType>) => {
      state.currentMic = action.payload;
    },
    setCurrentWebCamera: (state, action: PayloadAction<DeviceType>) => {
      state.currentWebcam = action.payload;
    },
    setCurrentSpeaker: (state, action: PayloadAction<DeviceType>) => {
      state.currentSpeaker = action.payload;
    },
    setMicrophonePermissionAllowed: (state, action: PayloadAction<boolean>) => {
      state.microphonePermissionAllowed = action.payload;
    },
    setCameraPermissionAllowed: (state, action: PayloadAction<boolean>) => {
      state.cameraPermissionAllowed = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(fetchToken.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchToken.fulfilled, (state, action) => {
      state.loading = false;
      state.token = action.payload.jwt;
      state.customRoomId = action.payload.room_id;
    });
    builder.addCase(createRoom.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(createRoom.fulfilled, (state, action) => {
      state.loading = false;
      state.roomId = action.payload.meetingId;
    });
    builder.addCase(logOut.fulfilled, (state) => {
      state.token = null;
      state.customRoomId = null;
      state.loading = false;
      state.roomId = null;
    });
  },
});

export const {
  endMeeting,
  startMeeting,
  setIsMicOn,
  setIsWebcamOn,
  setCurrentMicrophone,
  setCurrentWebCamera,
  setCurrentSpeaker,
  setMicrophonePermissionAllowed,
  setCameraPermissionAllowed
} = videoSlice.actions;

export const token = (state: RootState) => state.video.token;
export const roomId = (state: RootState) => state.video.roomId;
export const meetingStatus = (state: RootState) => state.video.isMeetingStarted;
export const isMicOn = (state: RootState) => state.video.isMicOn;
export const isWebcamOn = (state: RootState) => state.video.isWebcamOn;
export const currentMic = (state: RootState) => state.video.currentMic;
export const currentWebcam = (state: RootState) => state.video.currentWebcam;
export const currentSpeaker = (state: RootState) => state.video.currentSpeaker;
export const cameraPermissionAllowed = (state: RootState) => state.video.cameraPermissionAllowed;
export const microphonePermissionAllowed = (
  state: RootState
) => state.video.microphonePermissionAllowed;
